import { Paper, Text, Group, createStyles, Box, ActionIcon, ScrollArea, Table, Badge, Avatar } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useDasboardApi from '../../hooks/useDashboardApi';
import PostGeneralSelector from './selectors/PostGeneralSelector';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: '20px 0 10px'
	},
	text: {
		fontSize: 36,
		lineHeight: '42px',
		fontWeight: 300,
	},
	cardTitle: {
		fontWeight: 600,
		fontSize: 16,
		lineHeight: '20px',
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.xs}`,
	},
	th: {
		padding: '0 !important',
	},
}));

export default function PostsGeneral() {

	// State and hooks
	const { classes } = useStyles();
	const { getPostsInformation } = useDasboardApi();
	const postsGeneral = useSelector(state => state.dashboardData.postsGeneral);
	const [filters, setFilters] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [filterOptions, setFilterOptions] = useState([]);
	const [dateOrder, setDateOrder] = useState('');
	const navigate = useNavigate();

	// On load
	useEffect(() => {
		async function fetchData() {
			await getPostsInformation(filters);
		}
		fetchData();
		// eslint-disable-next-line
	}, []);

	// On data load
	useEffect(() => {

		// If not null
		if (postsGeneral) {

			// Get unique filters
			const uniqueFilters = postsGeneral.reduce((acc, curr) => {
				acc.categories = acc.categories.includes(curr.postCategory?.name) ? acc.categories : [...acc.categories, curr.postCategory?.name];
				return acc;
			}, { categories: [] });
			setFilterOptions(uniqueFilters);

			// Set data 1:1
			setFilteredData(postsGeneral);

		}

	}, [postsGeneral]);

	// Order by date
	const orderByDate = (dateType = 'startDate') => {

		// Order and update
		const newData = filteredData.slice().sort((a, b) => new Date(a[dateType]) - new Date(b[dateType]));
		setDateOrder(dateType);
		setFilteredData(newData);

	}

	// TH component
	const Th = ({ children }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				&nbsp;
			</Th>
			<Th>
			&nbsp;
			</Th>
			<Th>
				Desde
			</Th>
			<Th>
				Hasta
			</Th>
		</tr>
	);

	// Build rows
	const rows = filteredData?.map((element) => (
		<tr key={Math.random()}>
			<td>
				<Badge
					variant="light"
					color="grape"
				>
					{element.postCategory ? element.postCategory?.name : 'N/A'}
				</Badge>
			</td>
			<td>
				<Group spacing={8}>
					{/* Avatar */}
					<Avatar
						src={element.image}
						size="md"
					/>
					{/* Name */}
					<Text>
						{element.title}
					</Text>
				</Group>
			</td>
			<td>
				{element.startDate ? dayjs(new Date(element.startDate)).format('DD/MM/YYYY') : 'N/A'}
			</td>
			<td>
				{element.endDate ? dayjs(new Date(element.endDate)).format('DD/MM/YYYY') : 'N/A'}
			</td>
		</tr>
	));

	// Reset filters
	const resetFilters = () => {

		// Reset state filters
		setDateOrder('');
		setFilteredData(postsGeneral);
		setFilters([]);

	}

	// Category filters
	useEffect(() => {

		// If there are filters
		if (filters.length > 0) {

			// Filter
			setFilteredData(postsGeneral.filter(el => filters.includes(el.postCategory?.name)));

		} else {

			// Reset
			setFilteredData(postsGeneral);

		}

		// eslint-disable-next-line
	}, [filters]);

	return (
		<Paper
			withBorder
			p={12}
			style={{ position: 'relative' }}
			radius={18}
		>
			{/* Top */}
			<Group
				position="apart"
				noWrap
			>
				{/* Title */}
				<Text className={classes.cardTitle}>
					General
				</Text>
				{/* New */}
				<ActionIcon
					variant="filled"
					onClick={() => navigate('/posts?add=1')}
					color="mainPurple"
				>
					<IconPlus
						size={18}
						stroke={2}
					/>
				</ActionIcon>
			</Group>
			{/* Time & Filter */}
			<Group
				position="apart"
				mt={8}
				noWrap
				align="center"
			>
				{/* Empty */}
				&nbsp;
				{/* Filter */}
				<PostGeneralSelector
					selected={[filters, setFilters]}
					options={filterOptions}
					orderByDate={orderByDate}
					dateOrder={dateOrder}
					resetFilters={resetFilters}
				/>
			</Group>
			{/* Content */}
			<Box className={classes.container}>
				{/* Table */}
				<ScrollArea>
					<Table
						highlightOnHover
						sx={{ flex: 2 }}
					>
						<thead>{ths}</thead>
						<tbody>{rows}</tbody>
					</Table>
				</ScrollArea>
			</Box>
		</Paper>
	);

}
